import { http, publicRequest } from '@/api/http'
import type {
  AuthChallengeRequested,
  AuthChallengeVerified,
  AuthTokens,
  LinkedCompany,
  LoginRequest,
  LoginResult,
} from '@/types/api'

export const authService = {
  async login(body: LoginRequest) {
    const { data } = await http.post<LoginResult>('/auth/login', body, publicRequest)
    return data
  },

  async refresh(refreshToken: string) {
    const { data } = await http.post<AuthTokens>('/auth/refresh', { refresh_token: refreshToken }, publicRequest)
    return data
  },

  /**
   * Signs in to another linked company without the password: a new token
   * pair, and the current session ends on the server.
   */
  async switchCompany(companyId: string, accessToken?: string) {
    // The login company step passes its own token: it must not go through
    // the stored session's refresh.
    const config = accessToken ? { ...publicRequest, headers: { Authorization: `Bearer ${accessToken}` } } : undefined
    const { data } = await http.post<LoginResult>('/auth/switch-company', { company_id: companyId }, config)
    return data
  },

  /** The companies the current sign-in can switch to; is_current marks this one. */
  async companies() {
    const { data } = await http.get<LinkedCompany[] | null>('/me/companies')
    return data ?? []
  },

  /** Revokes the current server session. Needs the still-valid access token. */
  async logout() {
    await http.post('/auth/logout')
  },

  passwordReset: {
    async request(phone: string) {
      const { data } = await http.post<AuthChallengeRequested>('/auth/password-reset/request', { phone }, publicRequest)
      return data
    },
    async verify(challengeId: string, code: string) {
      const { data } = await http.post<AuthChallengeVerified>(
        '/auth/password-reset/verify',
        { challenge_id: challengeId, code },
        publicRequest,
      )
      return data
    },
    async confirm(challengeId: string, verificationToken: string, password: string) {
      await http.post(
        '/auth/password-reset/confirm',
        { challenge_id: challengeId, verification_token: verificationToken, password },
        publicRequest,
      )
    },
  },

  phoneChange: {
    async request(phone: string) {
      const { data } = await http.post<AuthChallengeRequested>('/auth/phone-change/request', { phone })
      return data
    },
    async verify(challengeId: string, code: string) {
      const { data } = await http.post<AuthChallengeVerified>('/auth/phone-change/verify', {
        challenge_id: challengeId,
        code,
      })
      return data
    },
    async confirm(challengeId: string, verificationToken: string) {
      await http.post('/auth/phone-change/confirm', {
        challenge_id: challengeId,
        verification_token: verificationToken,
      })
    },
  },
}
